import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { SyncButton } from '../../components/SyncButton';
import {
  getIntegrationsMercury,
  postIntegrationsMercury,
  putIntegrationsMercuryIntegrationId,
  deleteIntegrationsMercuryIntegrationId,
  postIntegrationsMercuryIntegrationIdTestConnection,
  postIntegrationsMercuryIntegrationIdSync,
  postIntegrationsMercuryIntegrationIdClearSync,
  getIntegrationsMercuryIntegrationIdAccounts,
} from '../../api/generated/arrayscapeGamingDashboardAPI';
import { MercuryAccount } from '../../api/generated/model';
import { useNotifications } from '../../contexts/NotificationContext';

const MercuryTab: React.FC = () => {
  const [mercuryIntegrations, setMercuryIntegrations] = useState<any[]>([]);
  const [isLoadingMercuryIntegrations, setIsLoadingMercuryIntegrations] = useState(false);
  const [mercuryError, setMercuryError] = useState<string | null>(null);

  // Modal state — shared for create and edit
  const [showModal, setShowModal] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<any | null>(null); // null = create mode
  const [isSaving, setIsSaving] = useState(false);

  // Form fields
  const [formName, setFormName] = useState('');
  const [formToken, setFormToken] = useState('');
  const [formSendMoneyToken, setFormSendMoneyToken] = useState('');
  const [formSyncAccounts, setFormSyncAccounts] = useState(true);
  const [formSyncTransactions, setFormSyncTransactions] = useState(true);
  const [showToken, setShowToken] = useState(false);
  const [showSendMoneyToken, setShowSendMoneyToken] = useState(false);
  const [formPaymentAccountId, setFormPaymentAccountId] = useState('');
  const [mercuryAccounts, setMercuryAccounts] = useState<MercuryAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  const isEditing = editingIntegration !== null;

  // Get active syncs to monitor completion
  const { activeSyncs } = useNotifications();

  // Ref to track previous sync states
  const previousSyncStatesRef = useRef<Map<string, string>>(new Map());

  // Fetch integrations on mount
  useEffect(() => {
    fetchMercuryIntegrations();
  }, []);

  // Monitor sync completion and refetch integration data
  useEffect(() => {
    activeSyncs.forEach((syncEvent, syncId) => {
      const prevStatus = previousSyncStatesRef.current.get(syncId);
      const currentStatus = syncEvent.status;

      if ((prevStatus === 'started' || prevStatus === 'progress') &&
          (currentStatus === 'completed' || currentStatus === 'failed')) {

        const integrationId = syncEvent.integration_id;

        if (mercuryIntegrations.some(int => int.id === integrationId)) {
          console.log(`[MercuryTab] Mercury sync ${currentStatus} for ${integrationId}, refetching integrations`);
          fetchMercuryIntegrations();
        }
      }

      previousSyncStatesRef.current.set(syncId, currentStatus);
    });
  }, [activeSyncs, mercuryIntegrations]);

  const resetForm = () => {
    setFormName('');
    setFormToken('');
    setFormSendMoneyToken('');
    setFormSyncAccounts(true);
    setFormSyncTransactions(true);
    setShowToken(false);
    setShowSendMoneyToken(false);
    setFormPaymentAccountId('');
    setMercuryAccounts([]);
    setMercuryError(null);
  };

  const handleOpenCreateModal = () => {
    setEditingIntegration(null);
    resetForm();
    setShowModal(true);
  };

  const handleOpenEditModal = async (integration: any) => {
    setEditingIntegration(integration);
    setFormName(integration.name || '');
    setFormToken('');
    setFormSendMoneyToken('');
    setFormSyncAccounts(integration.sync_accounts ?? true);
    setFormSyncTransactions(integration.sync_transactions ?? true);
    setShowToken(false);
    setShowSendMoneyToken(false);
    setFormPaymentAccountId(integration.payment_account_id || '');
    setMercuryError(null);
    setShowModal(true);

    // Fetch Mercury accounts for payment account selection
    setLoadingAccounts(true);
    try {
      const resp = await getIntegrationsMercuryIntegrationIdAccounts(integration.id);
      setMercuryAccounts(resp.data || []);
    } catch (err) {
      console.error('Failed to fetch Mercury accounts:', err);
      setMercuryAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingIntegration(null);
    resetForm();
  };

  const fetchMercuryIntegrations = async () => {
    setIsLoadingMercuryIntegrations(true);
    setMercuryError(null);
    try {
      const response = await getIntegrationsMercury();
      setMercuryIntegrations(response.integrations || []);
    } catch (error) {
      console.error('Error fetching Mercury integrations:', error);
      setMercuryError('Failed to fetch Mercury integrations');
    } finally {
      setIsLoadingMercuryIntegrations(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMercuryError(null);
    try {
      if (!formName.trim()) {
        setMercuryError('Please enter a name for this integration');
        setIsSaving(false);
        return;
      }

      if (isEditing) {
        // Edit mode — token fields are optional (leave blank to keep current)
        await putIntegrationsMercuryIntegrationId(editingIntegration.id, {
          name: formName,
          ...(formToken.trim() ? { token_string: formToken } : {}),
          sync_accounts: formSyncAccounts,
          sync_transactions: formSyncTransactions,
          ...(formSendMoneyToken.trim()
            ? { send_money_token_string: formSendMoneyToken }
            : editingIntegration.has_send_money_token
              ? {} // don't send field — keep existing
              : { send_money_token_string: '' }), // clear it
          payment_account_id: formPaymentAccountId || '',
        });
      } else {
        // Create mode — token is required
        if (!formToken.trim()) {
          setMercuryError('Please enter a Mercury Bank API token');
          setIsSaving(false);
          return;
        }

        await postIntegrationsMercury({
          token_string: formToken,
          name: formName,
          ...(formSendMoneyToken.trim() ? { send_money_token_string: formSendMoneyToken } : {}),
        });
      }

      handleCloseModal();
      await fetchMercuryIntegrations();
    } catch (error: any) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} Mercury integration:`, error);
      if (error.response?.status === 400) {
        setMercuryError('Token or name is invalid, or name already exists');
      } else {
        setMercuryError(`Failed to ${isEditing ? 'update' : 'create'} Mercury integration`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteMercuryIntegration = async (integrationId: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete the Mercury Bank integration "${name}"?`)) {
      return;
    }

    try {
      await deleteIntegrationsMercuryIntegrationId(integrationId);
      await fetchMercuryIntegrations();
    } catch (error) {
      console.error('Error deleting Mercury integration:', error);
      setMercuryError('Failed to delete Mercury integration');
    }
  };

  const handleTestMercuryConnection = async (integrationId: string, name: string) => {
    try {
      await postIntegrationsMercuryIntegrationIdTestConnection(integrationId);
      await fetchMercuryIntegrations();
    } catch (error: any) {
      console.error('Error testing Mercury connection:', error);
      await fetchMercuryIntegrations();
    }
  };

  const handleSyncMercuryIntegration = async (integrationId: string, name: string): Promise<string | undefined> => {
    try {
      const resp = await postIntegrationsMercuryIntegrationIdSync(integrationId);
      await fetchMercuryIntegrations();
      return (resp as any)?.sync_id;
    } catch (error: any) {
      console.error('Error triggering Mercury sync:', error);
      if (error.response?.status === 409) {
        setMercuryError('A sync is already in progress for this integration. If it appears stuck, use "Clear Sync Lock" to reset it.');
      }
      await fetchMercuryIntegrations();
      return undefined;
    }
  };

  const handleClearSyncLock = async (integrationId: string) => {
    if (!window.confirm('Clear the sync lock? Only do this if a sync appears permanently stuck.')) {
      return;
    }
    try {
      await postIntegrationsMercuryIntegrationIdClearSync(integrationId);
      setMercuryError(null);
      await fetchMercuryIntegrations();
    } catch (error: any) {
      console.error('Error clearing sync lock:', error);
      setMercuryError('Failed to clear sync lock');
    }
  };

  return (
    <>
      <Card className="">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center">
              <div className="ms-4 me-4 bg-header-control rounded p-2">
                <img src="/mercury_logo_vertical.png" alt="Mercury Bank" style={{ height: '4rem' }} />
              </div>
              <div>
                <h5 className="mb-1">Mercury Bank Integrations</h5>
                <p className="text-muted mb-0">Connect one or more Mercury Bank accounts</p>
              </div>
            </div>
            <Button
              variant="primary"
              onClick={handleOpenCreateModal}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Add Integration
            </Button>
          </div>

          {mercuryError && !showModal && (
            <Alert variant="danger" dismissible onClose={() => setMercuryError(null)}>
              {mercuryError}
            </Alert>
          )}

          {isLoadingMercuryIntegrations ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading integrations...</p>
            </div>
          ) : mercuryIntegrations.length === 0 ? (
            <div className="text-center py-5">
              <div className="d-inline-block bg-header-control rounded p-2">
                <img src="/mercury_logo_horizontal.png" alt="Mercury Bank" style={{ height: '4rem' }} />
              </div>
              <h6 className="mt-3 text-muted">No Mercury Bank Integrations</h6>
              <p className="text-muted">Add your first Mercury Bank integration to get started</p>
            </div>
          ) : (
            <div className="row">
              {mercuryIntegrations.map((integration) => (
                <div key={integration.id} className="col-md-6 mb-3">
                  <Card className="border">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">
                            {integration.name || 'Mercury Bank Account'}
                          </h6>
                        </div>
                        <div className="d-flex gap-1">
                          {integration.has_send_money_token && (
                            <span className="badge bg-info" title="Send Money token configured">
                              <i className="bi bi-send me-1"></i>Send
                            </span>
                          )}
                          <span className={`badge ${
                            integration.connection_status === 'connected' ? 'bg-success' :
                            integration.connection_status === 'error' ? 'bg-danger' : 'bg-secondary'
                          }`}>
                            {integration.connection_status === 'connected' ? 'Connected' :
                             integration.connection_status === 'error' ? 'Error' : 'Not Tested'}
                          </span>
                        </div>
                      </div>

                      <div className="d-flex gap-2 mb-3 flex-wrap">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleOpenEditModal(integration)}
                        >
                          <i className="bi bi-pencil me-1"></i>
                          Edit
                        </Button>
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleTestMercuryConnection(integration.id, integration.name)}
                          disabled={integration.status !== 'active'}
                        >
                          <i className="bi bi-check-circle me-1"></i>
                          Test Connection
                        </Button>
                        <SyncButton
                          claimId={`mercury-sync-${integration.id}`}
                          onClick={() => handleSyncMercuryIntegration(integration.id, integration.name)}
                          disabled={integration.status !== 'active'}
                        />
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteMercuryIntegration(integration.id, integration.name)}
                        >
                          <i className="bi bi-trash me-1"></i>
                          Delete
                        </Button>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => handleClearSyncLock(integration.id)}
                          title="Clear a stuck sync-in-progress lock"
                        >
                          <i className="bi bi-unlock me-1"></i>
                          Clear Sync Lock
                        </Button>
                      </div>

                      {integration.connection_message && (
                        <div className={`alert ${integration.connection_status === 'connected' ? 'alert-success' : 'alert-danger'} alert-sm mb-3`} role="alert">
                          <i className={`bi ${integration.connection_status === 'connected' ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-1`}></i>
                          <small>{integration.connection_message}</small>
                        </div>
                      )}

                      <div className="mt-3 pt-3 border-top">
                        <div className="row">
                          <div className="col">
                            <Form.Check
                              type="checkbox"
                              label="Sync Accounts"
                              checked={integration.sync_accounts}
                              disabled
                            />
                          </div>
                          <div className="col">
                            <Form.Check
                              type="checkbox"
                              label="Sync Transactions"
                              checked={integration.sync_transactions}
                              disabled
                            />
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Create / Edit Mercury Bank Integration Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Edit Mercury Bank Integration' : 'Add New Mercury Bank Integration'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mercuryError && (
            <Alert variant="danger" dismissible onClose={() => setMercuryError(null)}>
              {mercuryError}
            </Alert>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Integration Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Main Business Account"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              maxLength={100}
            />
            <Form.Text className="text-muted">
              Friendly name to identify this integration
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Mercury Bank API Token {!isEditing && <span className="text-danger">*</span>}</Form.Label>
            <div className="input-group">
              <Form.Control
                type={showToken ? "text" : "password"}
                placeholder={isEditing ? "Leave blank to keep current token" : "Enter your Mercury Bank API token"}
                value={formToken}
                onChange={(e) => setFormToken(e.target.value)}
                style={{ fontFamily: 'monospace' }}
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowToken(!showToken)}
              >
                <i className={`bi ${showToken ? 'bi-eye-slash' : 'bi-eye'}`}></i>
              </Button>
            </div>
            <Form.Text className="text-muted">
              Your Mercury Bank API token (usually 90+ characters). This will be encrypted and stored securely.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Send Money API Token <span className="text-muted">(optional)</span></Form.Label>
            <div className="input-group">
              <Form.Control
                type={showSendMoneyToken ? "text" : "password"}
                placeholder={isEditing && editingIntegration?.has_send_money_token
                  ? "Leave blank to keep current token"
                  : "Enter your Mercury send-money API token"}
                value={formSendMoneyToken}
                onChange={(e) => setFormSendMoneyToken(e.target.value)}
                style={{ fontFamily: 'monospace' }}
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowSendMoneyToken(!showSendMoneyToken)}
              >
                <i className={`bi ${showSendMoneyToken ? 'bi-eye-slash' : 'bi-eye'}`}></i>
              </Button>
            </div>
            <Form.Text className="text-muted">
              A separate token with "Send Money with Approval" scope. Required only if you want to send payments via Mercury.
              {isEditing && editingIntegration?.has_send_money_token && (
                <> Currently configured. Leave blank to keep, or enter a new token to replace.</>
              )}
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Sync Preferences</Form.Label>
            <div className="d-flex gap-4">
              <Form.Check
                type="checkbox"
                label="Sync Accounts"
                checked={formSyncAccounts}
                onChange={(e) => setFormSyncAccounts(e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="Sync Transactions"
                checked={formSyncTransactions}
                onChange={(e) => setFormSyncTransactions(e.target.checked)}
              />
            </div>
          </Form.Group>
          {isEditing && (
            <Form.Group className="mb-3">
              <Form.Label>Payment Account</Form.Label>
              {loadingAccounts ? (
                <div className="d-flex align-items-center gap-2 text-muted">
                  <Spinner animation="border" size="sm" />
                  Loading accounts...
                </div>
              ) : mercuryAccounts.length === 0 ? (
                <Form.Text className="text-muted d-block">
                  No accounts found. Test the connection first to verify access.
                </Form.Text>
              ) : (
                <>
                  <Form.Select
                    value={formPaymentAccountId}
                    onChange={(e) => setFormPaymentAccountId(e.target.value)}
                  >
                    <option value="">None (payments disabled)</option>
                    {mercuryAccounts.map(acct => (
                      <option key={acct.id} value={acct.id || ''}>
                        {acct.name || acct.id}{acct.kind ? ` (${acct.kind})` : ''}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Select the Mercury account to send payments from. Requires a send-money token.
                  </Form.Text>
                </>
              )}
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleSave}
            disabled={isSaving || !formName.trim()}
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                {isEditing ? 'Saving...' : 'Creating...'}
              </>
            ) : (
              <>
                <i className={`bi ${isEditing ? 'bi-check-lg' : 'bi-plus-circle'} me-1`}></i>
                {isEditing ? 'Save Changes' : 'Create Integration'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MercuryTab;
